import { useCallback, useEffect, useState } from "react";
import { Coins, ArrowsClockwise, CheckCircle, ClockCounterClockwise, Lock, LockKey } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * Ledger Corrections — two-fold purpose:
 *
 *   1. **Diagnostic**: re-prices every approved conversion under the
 *      legacy (DEFAULT_SPLIT=80) and the current (100% pass-through)
 *      rules and surfaces the diff per (publisher, brand).
 *
 *   2. **Point-in-time freeze**: a one-shot "Lock Past Earnings" action
 *      snapshots every approved conversion's pub_earning into
 *      `actions.frozen_pub_earning_usd` so subsequent config changes
 *      (e.g. lowering a brand's cap %) don't retroactively rewrite
 *      historical reports. Founder-approved permanent lock; no unlock
 *      button (intentional — any correction is a manual ledger
 *      adjustment outside this system).
 */
export default function AdminLedgerCorrections() {
  const [tab, setTab] = useState("outstanding");
  const [data, setData] = useState({ corrections: [], totals: {} });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settlingKey, setSettlingKey] = useState("");
  const [freezeStatus, setFreezeStatus] = useState(null);
  const [freezing, setFreezing] = useState(false);

  const [settlingAll, setSettlingAll] = useState(false);
  const [passthroughStatus, setPassthroughStatus] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const fetchPassthroughStatus = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/ledger-corrections/passthrough-upgrade-status");
      setPassthroughStatus(data || null);
    } catch (e) {
      setPassthroughStatus(null);
    }
  }, []);

  const restoreFixedPctModes = async () => {
    if (!passthroughStatus?.restore_rows) return;
    let preview;
    try {
      const { data } = await API.post(
        "/admin/ledger-corrections/restore-fixed-pct-modes",
        { dry_run: true },
      );
      preview = data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      return;
    }
    const brands = (passthroughStatus.restore_brands || []).slice(0, 5).join(", ");
    const more = (passthroughStatus.restore_brands || []).length > 5
      ? `, +${passthroughStatus.restore_brands.length - 5} more` : "";
    if (!window.confirm(
      `RESTORE FIXED % MODES — corrects a mis-flipped migration step.\n\n` +
      `WHAT THIS DOES:\n` +
      `  Publisher_brands rows that are currently Passthrough mode AND\n` +
      `  have a positive cap (custom_commission_pct > 0) AND are NOT\n` +
      `  on Skimlinks are restored to Fixed % mode. Passthrough doesn't\n` +
      `  use a cap, so a cap+passthrough combination is almost always\n` +
      `  a legacy-null row that got force-flipped — pubs were silently\n` +
      `  receiving the full network payout (kicker) instead of being\n` +
      `  capped at the configured pct.\n\n` +
      `WILL RESTORE:\n` +
      `  • ${preview.rows_restored.toLocaleString()} publisher_brands row(s)\n` +
      `  • ${preview.publishers_affected.toLocaleString()} publisher(s)\n` +
      `  • ${preview.brands_affected.toLocaleString()} brand(s): ${brands}${more}\n\n` +
      `AND RECONCILE:\n` +
      `  • Ledger corrections recomputed: ${preview.corrections_recomputed.toLocaleString()}\n` +
      `  • Ledger corrections deleted: ${preview.corrections_deleted.toLocaleString()}\n` +
      `  • Over-paid by previous migration: $${(preview.overpayment_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} ` +
      `(net off at next payout cycle)\n\n` +
      `Click OK to execute, or Cancel to abort.`
    )) return;
    setRestoring(true);
    try {
      const { data } = await API.post(
        "/admin/ledger-corrections/restore-fixed-pct-modes",
        {},
      );
      toast.success(
        `Restored ${data.rows_restored.toLocaleString()} row(s) to Fixed % · ` +
        `Recomputed ${data.corrections_recomputed.toLocaleString()}, ` +
        `deleted ${data.corrections_deleted.toLocaleString()} ledger corrections` +
        (data.overpayment_usd > 0 ? ` · Over-paid $${data.overpayment_usd.toFixed(2)} (net at next payout)` : "")
      );
      fetchPassthroughStatus();
      fetchOutstanding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const upgradePassthrough = async () => {
    if (!passthroughStatus?.rows_pending) return;
    // Show dry-run preview first so admin sees exact $ before committing.
    let preview;
    try {
      const { data } = await API.post(
        "/admin/ledger-corrections/upgrade-passthrough-splits",
        { dry_run: true },
      );
      preview = data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      return;
    }
    const splitsList = Object.entries(preview.splits_seen || {})
      .map(([k, v]) => `${k}% × ${v} rows`).join("\n  ");
    if (!window.confirm(
      `UPGRADE PASSTHROUGH SPLITS → 100% — irreversible policy migration.\n\n` +
      `WHAT THIS DOES:\n` +
      `  Every publisher_brands row in Passthrough mode with a split < 100\n` +
      `  is flipped to 100. The 20% (or other) team cut moves to offline\n` +
      `  invoicing per the new policy. Each affected publisher is back-paid\n` +
      `  the diff for every approved past action they were short-changed on.\n\n` +
      `WILL TOUCH:\n` +
      `  • ${preview.splits_updated.toLocaleString()} publisher_brands row(s)\n` +
      `  • ${preview.publishers_affected.toLocaleString()} publisher(s)\n` +
      `  • Split breakdown:\n  ${splitsList}\n\n` +
      `WILL BACK-PAY:\n` +
      `  • ${preview.actions_back_paid.toLocaleString()} approved action(s)\n` +
      `  • Total owed: $${(preview.total_owed_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}\n\n` +
      `WILL NOT TOUCH:\n` +
      `  • Fixed % / Flat CPA $ / CPC rows (preserved, regardless of split)\n\n` +
      `Click OK to execute (writes to publisher_brands + actions + ledger_corrections), ` +
      `or Cancel to abort.`
    )) return;
    setUpgrading(true);
    try {
      const { data } = await API.post(
        "/admin/ledger-corrections/upgrade-passthrough-splits",
        {},
      );
      toast.success(
        `Upgraded ${data.splits_updated.toLocaleString()} splits → 100% · ` +
        `Back-paid $${(data.total_owed_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} ` +
        `across ${data.actions_back_paid.toLocaleString()} action(s) · ` +
        `Re-locked ${data.frozen_rows_relocked.toLocaleString()} frozen rows`
      );
      fetchPassthroughStatus();
      fetchOutstanding();
      fetchFreezeStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Passthrough upgrade failed");
    } finally {
      setUpgrading(false);
    }
  };

  const fetchOutstanding = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/ledger-corrections");
      setData(data || { corrections: [], totals: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load corrections");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/ledger-corrections/history");
      setHistory(data?.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFreezeStatus = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/ledger-corrections/freeze-status");
      setFreezeStatus(data || null);
    } catch (e) {
      // Status is informational only — don't toast on failure.
      setFreezeStatus(null);
    }
  }, []);

  const lockPastEarnings = async () => {
    if (!window.confirm(
      "Lock past earnings — PERMANENT, NO UNLOCK.\n\n" +
      "This snapshots every approved conversion's pub_earning into the\n" +
      "actions table. After this runs:\n\n" +
      "  • Today's config produces today's locked numbers.\n" +
      "  • Tomorrow's cap/split changes WILL NOT touch the locked rows.\n" +
      "  • New conversions (event_date > today) keep computing live until\n" +
      "    you run Lock again on a future date.\n\n" +
      "Any future correction to a locked row must be a MANUAL LEDGER\n" +
      "ADJUSTMENT outside this system.\n\n" +
      "Type OK to confirm — or cancel."
    )) return;
    setFreezing(true);
    try {
      const { data } = await API.post("/admin/ledger-corrections/freeze");
      toast.success(
        `Locked ${data.frozen_count.toLocaleString()} actions ($${(data.total_locked_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })})` +
        (data.skipped_already_frozen ? ` — ${data.skipped_already_frozen} already-locked rows skipped` : "")
      );
      fetchFreezeStatus();
      fetchOutstanding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to lock past earnings");
    } finally {
      setFreezing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      // Always refresh freeze + passthrough status — they power their
      // respective banners regardless of which tab is active.
      fetchFreezeStatus();
      fetchPassthroughStatus();
      if (tab === "outstanding") await fetchOutstanding();
      else await fetchHistory();
    };
    run();
    return () => { cancelled = true; };
  }, [tab, fetchOutstanding, fetchHistory, fetchFreezeStatus, fetchPassthroughStatus]);

  const settle = async (row) => {
    if (!window.confirm(
      `Mark ${row.action_count} ${row.brand_name} conversion(s) as PAID for ` +
      `${row.publisher_email || row.publisher_id}?\n\n` +
      `Owed: $${row.owed_amount.toFixed(2)}\n\n` +
      `This records the back-pay in the audit ledger. Settle ONLY after ` +
      `the actual wire/payout has been sent.`
    )) return;
    const key = `${row.publisher_id}|${row.brand_name}`;
    setSettlingKey(key);
    try {
      const { data } = await API.post("/admin/ledger-corrections/settle", {
        publisher_id: row.publisher_id,
        brand_name: row.brand_name,
        note: `Back-pay for legacy 80% default-split (${row.action_count} actions)`,
      });
      toast.success(`Settled ${data.settled} corrections`);
      fetchOutstanding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to settle");
    } finally {
      setSettlingKey("");
    }
  };

  const payAllAndFreeze = async () => {
    const owed = (t.owed_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
    const actions = (t.action_count || 0).toLocaleString();
    const pubs = (t.publisher_count || 0).toLocaleString();
    if (!window.confirm(
      `PAY ALL OUTSTANDING + FREEZE — two-step bulk action.\n\n` +
      `Step 1 — Mark ALL back-owed corrections as PAID:\n` +
      `  • ${actions} action(s) across ${pubs} publisher(s)\n` +
      `  • Total back-pay: $${owed}\n\n` +
      `Step 2 — Lock past earnings (PERMANENT, NO UNLOCK):\n` +
      `  • Snapshots every approved conversion's pub_earning into actions.\n` +
      `  • Future config changes (cap %, split, payout mode) will NOT touch\n` +
      `    these locked rows.\n\n` +
      `Settle ONLY after the actual wire/payout has been sent. ` +
      `Click OK to proceed, or Cancel to abort.`
    )) return;
    setSettlingAll(true);
    try {
      const { data: sd } = await API.post("/admin/ledger-corrections/settle-all", {
        note: "Bulk back-pay + freeze",
      });
      const { data: fd } = await API.post("/admin/ledger-corrections/freeze");
      toast.success(
        `Paid $${(sd.total_owed_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} ` +
        `across ${sd.settled.toLocaleString()} actions · ` +
        `Locked ${fd.frozen_count.toLocaleString()} actions ` +
        `($${(fd.total_locked_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })})`
      );
      fetchOutstanding();
      fetchFreezeStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pay-all-and-freeze failed");
    } finally {
      setSettlingAll(false);
    }
  };

  const t = data.totals || {};

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ledger-corrections-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Coins size={28} weight="duotone" className="text-amber-600" />
            Ledger Corrections
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Publishers historically underpaid by the legacy 80% default-split
            policy. Each row recomputes the conversion under the new 100%
            pass-through rule and shows the diff we owe.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => (tab === "outstanding" ? fetchOutstanding() : fetchHistory())}
          data-testid="ledger-refresh-btn"
        >
          <ArrowsClockwise size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Freeze banner — shows lock status + the one-shot "Lock Past
          Earnings" action. Indigo when there are still-floating eligible
          rows, slate when everything is already locked. */}
      {freezeStatus && (
        <div
          className={`mb-6 rounded-md border px-4 py-3 flex items-start gap-3 ${
            freezeStatus.floating_count_eligible > 0
              ? "border-indigo-200 bg-indigo-50/60"
              : "border-emerald-200 bg-emerald-50/40"
          }`}
          data-testid="ledger-freeze-banner"
        >
          <div className="shrink-0 w-10 h-10 rounded-md bg-white border border-slate-200 flex items-center justify-center">
            {freezeStatus.floating_count_eligible > 0
              ? <Lock size={20} className="text-indigo-700" weight="duotone" />
              : <LockKey size={20} className="text-emerald-700" weight="duotone" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              {freezeStatus.floating_count_eligible > 0
                ? "Past earnings — not yet locked"
                : "All historical earnings are locked"}
            </div>
            <div className="text-xs text-slate-600 mt-0.5 leading-snug">
              <span className="font-mono font-bold text-slate-900">{freezeStatus.frozen_count.toLocaleString()}</span> action(s) currently locked
              {freezeStatus.floating_count_eligible > 0 && (
                <> · <span className="font-mono font-bold text-indigo-700">{freezeStatus.floating_count_eligible.toLocaleString()}</span> eligible to lock (approved, event_date ≤ today)</>
              )}
              {freezeStatus.last_freeze_at && (
                <> · last lock: <span className="font-mono">{freezeStatus.last_freeze_at.slice(0, 19).replace("T", " ")} UTC</span></>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-1 leading-snug">
              Locking snapshots each approved conversion&apos;s pub_earning into the actions table.
              Future config changes (cap %, split, payout mode) will <strong>not</strong> rewrite these rows.
              <strong className="text-rose-700"> Permanent — no unlock button.</strong>
            </div>
          </div>
          {freezeStatus.floating_count_eligible > 0 && (
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
              disabled={freezing}
              onClick={lockPastEarnings}
              data-testid="ledger-lock-btn"
            >
              <Lock size={14} className="mr-1.5" weight="duotone" />
              {freezing ? "Locking…" : `Lock ${freezeStatus.floating_count_eligible.toLocaleString()} past earnings`}
            </Button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center border-b border-slate-200 mb-6">
        {[
          { id: "outstanding", label: "Outstanding", icon: Coins },
          { id: "settled", label: "Settled", icon: ClockCounterClockwise },
        ].map((it) => (
          <button
            key={it.id}
            onClick={() => setTab(it.id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === it.id
                ? "border-[#002FA7] text-[#002FA7]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`ledger-tab-${it.id}`}
          >
            <it.icon size={14} className="inline mr-1.5 -mt-0.5" />
            {it.label}
          </button>
        ))}
      </div>

      {tab === "outstanding" && (
        <>
          {/* Recovery banner — only shows when there are mis-flipped
              fixed_pct brands now showing as passthrough. Higher priority
              than the upgrade banner because it represents pubs receiving
              MORE than the configured cap (real $ leak). */}
          {passthroughStatus?.restore_rows > 0 && (
            <div className="mb-5 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 flex items-start gap-3" data-testid="restore-fixed-pct-banner">
              <div className="shrink-0 w-10 h-10 rounded-md bg-white border border-rose-300 flex items-center justify-center">
                <Lock size={20} weight="duotone" className="text-rose-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-rose-900">
                  Action required — restore Fixed % modes for {passthroughStatus.restore_rows.toLocaleString()} row(s)
                </div>
                <div className="text-xs text-rose-800 mt-0.5 leading-snug">
                  These rows are currently Passthrough mode but have a positive cap (custom_commission_pct &gt; 0).
                  Brand(s): <span className="font-mono">{(passthroughStatus.restore_brands || []).slice(0, 4).join(", ")}{(passthroughStatus.restore_brands || []).length > 4 ? `, +${passthroughStatus.restore_brands.length - 4} more` : ""}</span>.
                </div>
                <div className="text-[11px] text-rose-700 mt-1 leading-snug">
                  Passthrough doesn&apos;t honor the cap — these pubs are silently receiving the full network payout (kicker) above the configured cap.
                  Click to restore Fixed % and reconcile any over-paid ledger corrections.
                </div>
              </div>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white shrink-0"
                disabled={restoring}
                onClick={restoreFixedPctModes}
                data-testid="restore-fixed-pct-btn"
              >
                <Lock size={14} className="mr-1.5" weight="duotone" />
                {restoring ? "Restoring…" : "Restore Fixed %"}
              </Button>
            </div>
          )}

          {/* Passthrough Upgrade — one-shot policy migration. Only shown
              when there are publisher_brands rows still on a legacy
              non-100 passthrough split. Disappears once executed. */}
          {passthroughStatus?.rows_pending > 0 && (
            <div className="mb-5 rounded-md border border-indigo-200 bg-indigo-50/60 px-4 py-3 flex items-start gap-3" data-testid="passthrough-upgrade-banner">
              <div className="shrink-0 w-10 h-10 rounded-md bg-white border border-indigo-200 flex items-center justify-center">
                <ArrowsClockwise size={20} weight="duotone" className="text-indigo-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  Upgrade legacy Passthrough splits → 100%
                </div>
                <div className="text-xs text-slate-600 mt-0.5 leading-snug">
                  <span className="font-mono font-bold text-slate-900">{passthroughStatus.rows_pending.toLocaleString()}</span> publisher_brands row(s) across{" "}
                  <span className="font-mono font-bold text-slate-900">{passthroughStatus.publishers_pending.toLocaleString()}</span> publisher(s) are still on legacy splits{" "}
                  ({Object.entries(passthroughStatus.splits_seen || {}).map(([k, v]) => `${k}%×${v}`).join(", ")}).
                  The 20% team margin moves to offline invoicing — pubs get the full network payout. Back-pay diffs are auto-written to ledger.
                </div>
                <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                  <strong>Will NOT touch</strong> Fixed % / Flat CPA $ / CPC rows.
                  Click to preview exact $ before committing.
                </div>
              </div>
              <Button
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                disabled={upgrading}
                onClick={upgradePassthrough}
                data-testid="passthrough-upgrade-btn"
              >
                <ArrowsClockwise size={14} className="mr-1.5" weight="duotone" />
                {upgrading ? "Upgrading…" : "Upgrade to 100%"}
              </Button>
            </div>
          )}

          {/* Bulk action — Pay All Outstanding + Freeze */}
          {!loading && data.corrections?.length > 0 && (
            <div className="mb-5 rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-3" data-testid="ledger-pay-all-banner">
              <div className="shrink-0 w-10 h-10 rounded-md bg-white border border-amber-200 flex items-center justify-center">
                <CheckCircle size={20} weight="duotone" className="text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  Pay all outstanding back-owed earnings & freeze
                </div>
                <div className="text-xs text-slate-600 mt-0.5 leading-snug">
                  Settles <span className="font-mono font-bold text-slate-900">{(t.action_count || 0).toLocaleString()}</span> action(s) across{" "}
                  <span className="font-mono font-bold text-slate-900">{(t.publisher_count || 0).toLocaleString()}</span> publisher(s) totalling{" "}
                  <span className="font-mono font-bold text-amber-700">${(t.owed_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>,
                  then locks every approved past earning so future config changes never retro-reprice these rows.
                </div>
                <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Use this when the wire transfer for the back-pay has been sent. Both steps are recorded in the audit ledger.
                  <strong className="text-rose-700"> The freeze step is permanent — no unlock.</strong>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                disabled={settlingAll}
                onClick={payAllAndFreeze}
                data-testid="ledger-pay-all-and-freeze-btn"
              >
                <CheckCircle size={14} className="mr-1.5" weight="duotone" />
                {settlingAll ? "Paying & locking…" : "Pay all & Freeze"}
              </Button>
            </div>
          )}

          {/* Summary tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <Tile
              label="Total Owed"
              value={`$${(t.owed_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
              tone="primary"
              testId="ledger-tile-owed"
            />
            <Tile
              label="Action Count"
              value={(t.action_count || 0).toLocaleString()}
              testId="ledger-tile-actions"
            />
            <Tile
              label="Publishers Affected"
              value={(t.publisher_count || 0).toLocaleString()}
              testId="ledger-tile-publishers"
            />
          </div>

          {loading ? (
            <CenteredMsg msg="Loading…" />
          ) : !data.corrections?.length ? (
            <CenteredMsg msg="🎉 No outstanding underpayments. Ledger is clean." testId="ledger-empty" />
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/40">
                      <Th>Publisher</Th>
                      <Th>Brand</Th>
                      <Th align="right">Actions</Th>
                      <Th align="right">Previously Paid</Th>
                      <Th align="right">Should Be</Th>
                      <Th align="right">Owed</Th>
                      <Th>Date Range</Th>
                      <Th align="right">Action</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.corrections.map((row) => {
                      const key = `${row.publisher_id}|${row.brand_name}`;
                      return (
                        <tr key={key} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`ledger-row-${key}`}>
                          <td className="px-4 py-2.5 font-medium text-slate-900">{row.publisher_email || row.publisher_id}</td>
                          <td className="px-4 py-2.5 text-slate-700">{row.brand_name}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-slate-700">{row.action_count}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-slate-500">${row.old_total.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-slate-900">${row.new_total.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-bold text-amber-600">${row.owed_amount.toFixed(2)}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{row.earliest_date || row.latest_date ? `${row.earliest_date || "—"} → ${row.latest_date || "—"}` : "—"}</td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={settlingKey === key}
                              onClick={() => settle(row)}
                              data-testid={`ledger-settle-${key}`}
                            >
                              <CheckCircle size={14} className="mr-1.5" />
                              {settlingKey === key ? "…" : "Mark Paid"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "settled" && (
        <>
          {loading ? (
            <CenteredMsg msg="Loading…" />
          ) : !history.length ? (
            <CenteredMsg msg="No settled corrections yet." testId="ledger-history-empty" />
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/40">
                      <Th>Settled Date</Th>
                      <Th>Publisher</Th>
                      <Th>Brand</Th>
                      <Th align="right">Actions</Th>
                      <Th align="right">Back-Paid</Th>
                      <Th>Settled By</Th>
                      <Th>Note</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} className="border-b border-slate-100" data-testid={`ledger-history-row-${i}`}>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{h.settled_date}</td>
                        <td className="px-4 py-2.5 text-slate-700">{h.publisher_email || h.publisher_id}</td>
                        <td className="px-4 py-2.5 text-slate-700">{h.brand_name}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-700">{h.action_count}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-emerald-700">${h.owed_total.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-600">{h.settled_by}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500">{h.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, tone, testId }) {
  const isPrimary = tone === "primary";
  return (
    <div
      className={`border rounded-md p-4 ${
        isPrimary
          ? "bg-amber-50 border-amber-200"
          : "bg-white border-slate-200"
      }`}
      data-testid={testId}
    >
      <div className={`text-[11px] uppercase tracking-[0.2em] font-bold mb-1 ${isPrimary ? "text-amber-700" : "text-slate-500"}`}>
        {label}
      </div>
      <div className={`text-xl font-bold font-mono ${isPrimary ? "text-amber-700" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className={`text-${align} px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold`}>
      {children}
    </th>
  );
}

function CenteredMsg({ msg, testId }) {
  return (
    <div className="border border-slate-200 rounded-md p-12 text-center text-sm text-slate-500" data-testid={testId}>
      {msg}
    </div>
  );
}
